import {
  ActionIcon,
  Box,
  Button,
  Center,
  Divider,
  Group,
  LoadingOverlay,
  Loader,
  MultiSelect,
  Stack,
  Switch,
  Tabs,
  Text as MantineText,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { closeAllModals, openModal } from '@mantine/modals';
import { IconChevronDown, IconEdit, IconPlus, IconX } from '@tabler/icons';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import BookitupAddIcon from '../../../../Atoms/BookitupAddIcon';
import { openDeleteConfirmModal } from '../../../../Atoms/DeleteConfirmModal';
import ConfigOption from '../../../../Atoms/ConfigOption';
import { CustomAttributeS } from '../../../../Service/Context/CustomAttributeS';
import { DataS } from '../../../../Service/DataS';
import { EventS } from '../../../../Service/EventS';
import { useEventKinds } from '../../../../Service/hooks/useEventKinds';
import { useEventStates } from '../../../../Service/hooks/useEventStates';
import { CalDavS } from '../../../../Service/CalDavS';
import { ThemeText } from '../../../../Atoms/ThemeText';
import { CustomAttributeWithGroupNameDTO } from '../../../../Types/CustomAttributeT';
import BookingState from '../../../../Types/Enums/BookingState';
import { CustomEventState } from '../../../../Types/EventT';
import { CalendarSubscription, CalendarSubscriptionPatch } from '../../../../Types/CalDavT';
import { VisibilityScope } from '../../../../Types/GoogleT';
import { BookitupTutorialButton } from '../../../../Atoms/BookitupTutorialIcon';
import { useJwtClaims } from '../../../../Service/Context/AuthContext';
import { LicenceS } from '../../../../Service/LicenceS';
import { Subscription } from './Subscription';

const RECOMMENDATIONS_TAB = 'recommendations';

const calendarBookingStates = [BookingState.OPEN, BookingState.OFFERED, BookingState.BOOKED];

const isCalendarBookingState = (state: BookingState): boolean => calendarBookingStates.includes(state);

const visibilityScopeOptions = [
  {
    value: VisibilityScope.CUSTOMER_DATA,
    title: 'Kundendaten anzeigen',
    description: 'Kalendereinträge enthalten Kundendaten wie Name, Firma und Kontaktdaten.',
  },
  {
    value: VisibilityScope.EVENT_NOTES,
    title: 'Notizen anzeigen',
    description: 'Kalendereinträge enthalten Deine Event-Notizen.',
  },
  {
    value: VisibilityScope.EVENT_KIND,
    title: 'Auftragsart anzeigen',
    description: 'Kalendereinträge enthalten die Auftragsart der Buchung.',
  },
  {
    value: VisibilityScope.EVENT_STATE,
    title: 'Buchungsstatus anzeigen',
    description: 'Kalendereinträge enthalten den Buchungsstatus der Buchung.',
  },
  {
    value: VisibilityScope.EVENT_NAME,
    title: 'Event-Name anzeigen',
    description: 'Kalendereinträge enthalten den Namen der Buchung.',
  },
  {
    value: VisibilityScope.EVENT_DESCRIPTION,
    title: 'Event-Beschreibung anzeigen',
    description: 'Kalendereinträge enthalten die Beschreibung der Buchung.',
  },
  {
    value: VisibilityScope.LOCATION,
    title: 'Location anzeigen',
    description: 'Kalendereinträge enthalten die hinterlegte Location und Adresse.',
  },
];

const onlyLocationCityVisibilityScopeOption = {
  value: VisibilityScope.ONLY_LOCATION_CITY,
  title: 'Nur Stadt der Location anzeigen',
  description:
    'Kalendereinträge enthalten nur die Stadt der Location. Straße, Hausnummer und Postleitzahl werden ausgeblendet.',
};

const getCustomStateOptions = (states: CustomEventState[]) =>
  EventS.sortCustomStates(states)
    .filter((state) => !!state.id && isCalendarBookingState(state.originState))
    .map((state) => ({ value: state.id as string, label: state.label }));

const getCalendarBookingStateOptions = () =>
  DataS.getBookingStateOptions().filter(({ value }) => isCalendarBookingState(value));

const filterCalendarBookingStates = (states: BookingState[]) => states.filter(isCalendarBookingState);

const getSubscriptionLabel = (subscription: CalendarSubscription, index: number) =>
  subscription.name || `Kalender ${index + 1}`;

const isPrimarySubscription = (index: number) => index === 0;

const SubscriptionCalendar: FC = () => {
  const [loading, setLoading] = useState(false);
  const { licence: userLicence } = useJwtClaims();
  const [savingField, setSavingField] = useState<keyof CalendarSubscriptionPatch | null>(null);
  const [savingVisibilityScope, setSavingVisibilityScope] = useState<VisibilityScope | null>(null);
  const [subscriptions, setSubscriptions] = useState<CalendarSubscription[]>([]);
  const [activeSubscriptionId, setActiveSubscriptionId] = useState<string | null>(null);
  const { loading: eventStatesLoading, states: eventStates } = useEventStates();
  const { loading: eventKindsLoading, kinds } = useEventKinds();
  const [customAttributesLoading, setCustomAttributesLoading] = useState(false);
  const [customAttributes, setCustomAttributes] = useState<CustomAttributeWithGroupNameDTO[]>([]);
  const hasFlowLicence = useMemo(() => LicenceS.hasSuitableLicence('FLOW', userLicence), [userLicence]);
  const recommendationsSubscription = subscriptions[0] ?? null;
  const subscription =
    activeSubscriptionId === RECOMMENDATIONS_TAB
      ? null
      : subscriptions.find(({ id }) => id === activeSubscriptionId) ?? null;
  const canConfigureSubscription = hasFlowLicence && !!subscription;

  const getCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const nextSubscriptions = await CalDavS.getCalendarSubscriptions({ limit: hasFlowLicence ? undefined : 1 });
      setSubscriptions(nextSubscriptions);
      setActiveSubscriptionId(nextSubscriptions[0]?.id ?? null);
    } catch {
      setSubscriptions([]);
      setActiveSubscriptionId(null);
    } finally {
      setLoading(false);
    }
  }, [hasFlowLicence]);

  useEffect(() => {
    getCalendar();
  }, [getCalendar]);

  useEffect(() => {
    setCustomAttributesLoading(true);
    CustomAttributeS.fetchAllAttributes()
      .then(setCustomAttributes)
      .catch(() => setCustomAttributes([]))
      .finally(() => setCustomAttributesLoading(false));
  }, []);

  const addCalendarSubscription = (calendarSubscription: CalendarSubscription) => {
    setSubscriptions((currentSubscriptions) => [...currentSubscriptions, calendarSubscription]);
    setActiveSubscriptionId(calendarSubscription.id);
  };

  const updateCalendarSubscription = (calendarSubscription: CalendarSubscription) => {
    setSubscriptions((currentSubscriptions) =>
      currentSubscriptions.map((currentSubscription) =>
        currentSubscription.id === calendarSubscription.id ? calendarSubscription : currentSubscription,
      ),
    );
  };

  const openAddCalendarSubscriptionModal = () =>
    openCreateCalendarSubscriptionModal({
      defaultName: `Kalender ${subscriptions.length + 1}`,
      onCreate: addCalendarSubscription,
    });

  const openEditCalendarSubscriptionModal = (calendarSubscription: CalendarSubscription) =>
    openCalendarSubscriptionNameModal({
      calendarSubscription,
      onChange: updateCalendarSubscription,
    });

  const deleteCalendarSubscription = async (calendarSubscription: CalendarSubscription) => {
    const deleted = await CalDavS.deleteCalendarSubscription(calendarSubscription.id);
    if (deleted) {
      setSubscriptions((currentSubscriptions) => {
        const removedIndex = currentSubscriptions.findIndex(({ id }) => id === calendarSubscription.id);
        const nextSubscriptions = currentSubscriptions.filter(({ id }) => id !== calendarSubscription.id);
        setActiveSubscriptionId(
          activeSubscriptionId === RECOMMENDATIONS_TAB && nextSubscriptions.length > 0
            ? RECOMMENDATIONS_TAB
            : nextSubscriptions[Math.max(removedIndex - 1, 0)]?.id ?? null,
        );
        return nextSubscriptions;
      });
    }
    return deleted;
  };

  const openDeleteCalendarSubscriptionModal = (calendarSubscription: CalendarSubscription) =>
    openDeleteConfirmModal({
      title: 'Kalender-Abo löschen?',
      description: `Soll das Kalender-Abo "${calendarSubscription.name}" wirklich gelöscht werden?`,
      onConfirm: () => deleteCalendarSubscription(calendarSubscription),
    });

  const editCalendar = async (patch: CalendarSubscriptionPatch, calendarSubscription = subscription) => {
    if (!calendarSubscription) {
      return;
    }

    const field = Object.keys(patch)[0] as keyof CalendarSubscriptionPatch;
    setSavingField(field);
    try {
      const nextSubscription = await CalDavS.editCalendarSubscription(calendarSubscription.id, patch);
      if (nextSubscription) {
        updateCalendarSubscription(nextSubscription);
      }
    } finally {
      setSavingField(null);
    }
  };

  const bookingStateOptions = getCalendarBookingStateOptions();
  const customStateOptions = getCustomStateOptions(eventStates);
  const allowedCustomStateIds = customStateOptions.map(({ value }) => value);
  const statesOfInterest = filterCalendarBookingStates(subscription?.statesOfInterest ?? []);
  const customStatesOfInterest = (subscription?.customStatesOfInterest ?? []).filter((stateId) =>
    allowedCustomStateIds.includes(stateId),
  );
  const customAttributeOptions = DataS.getCustomAttributeOptionsV2(customAttributes);
  const allowedCustomFieldNames = customAttributeOptions.map(({ value }) => value);
  const customFields = (subscription?.customFields ?? []).filter((fieldName) =>
    allowedCustomFieldNames.includes(fieldName),
  );
  const visibilityScopes = subscription?.visibilityScopes ?? [];

  const toggleVisibilityScope = async (scope: VisibilityScope) => {
    if (!subscription) {
      return;
    }

    const isEnabled = visibilityScopes.includes(scope);
    let nextVisibilityScopes: VisibilityScope[];

    if (scope === VisibilityScope.LOCATION && isEnabled) {
      nextVisibilityScopes = visibilityScopes.filter(
        (visibilityScope) =>
          visibilityScope !== VisibilityScope.LOCATION && visibilityScope !== VisibilityScope.ONLY_LOCATION_CITY,
      );
    } else if (isEnabled) {
      nextVisibilityScopes = visibilityScopes.filter((visibilityScope) => visibilityScope !== scope);
    } else {
      nextVisibilityScopes = [...visibilityScopes, scope];
    }

    setSavingVisibilityScope(scope);
    try {
      const nextSubscription = await CalDavS.editCalendarSubscription(subscription.id, {
        visibilityScopes: nextVisibilityScopes,
      });
      if (nextSubscription) {
        updateCalendarSubscription(nextSubscription);
      }
    } finally {
      setSavingVisibilityScope(null);
    }
  };

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} />
      <Stack spacing="lg">
        <div>
          <ThemeText weight={600}>Erstelle Kalender-Abos für Deine bookitup Buchungen und Vermittlungen.</ThemeText>
          <MantineText color="dimmed" mt={5}>
            Übernommen werden Termine mit dem Status Anfrage, Option oder Gebucht; annullierte Termine werden aus dem
            Abo entfernt.
          </MantineText>
          <ThemeText size="sm" weight={600} mt="xl" mb={5}>
            Das Abo funktioniert mit Apple Kalender, Google Kalender, Outlook und vielen weiteren Kalender-Apps.
          </ThemeText>
          <MantineText color="dimmed">
            Mac: Kalender öffnen, Ablage wählen, Neues Kalender-Abo anklicken und die kopierte URL einfügen.
          </MantineText>
          <MantineText color="dimmed" mb="xs">
            iPhone oder iPad: Einstellungen öffnen, Kalender, Accounts, Account hinzufügen, Andere, Kalenderabo
            hinzufügen und die URL einfügen.
          </MantineText>
          <BookitupTutorialButton
            videoUrl="https://app.bookitup.de/academy/channels/perfect-overview?episodeId=mlqjdnri0n"
            name="AddCalendarAbo"
            label="Kalender Abo in iCloud/Outlook"
          />
        </div>
        {!loading && subscriptions.length === 0 && (
          <Center>
            {hasFlowLicence ? (
              <Button
                compact
                leftIcon={<IconPlus size={16} />}
                mt="sm"
                variant="subtle"
                onClick={openAddCalendarSubscriptionModal}
                sx={{ width: 300 }}
              >
                Kalender-Abo hinzufügen
              </Button>
            ) : (
              <MantineText color="dimmed" mt="sm">
                Es ist noch kein Kalender-Abo vorhanden.
              </MantineText>
            )}
          </Center>
        )}
        {subscriptions.length > 0 && (
          <Stack spacing="md" mt="sm">
            <Tabs
              value={activeSubscriptionId}
              onTabChange={(value) => value && setActiveSubscriptionId(value)}
              styles={{
                tab: {
                  '&[data-active]': {
                    fontWeight: 'bolder',
                  },
                  '&:has(.calendar-subscription-action:hover)': {
                    backgroundColor: 'transparent',
                  },
                },
              }}
            >
              <Group align="flex-start" noWrap spacing="xs">
                <Tabs.List sx={{ flex: 1 }}>
                  {subscriptions.map((calendarSubscription, index) => (
                    <Tabs.Tab key={calendarSubscription.id} value={calendarSubscription.id}>
                      <Group spacing={10} noWrap>
                        <span>{getSubscriptionLabel(calendarSubscription, index)}</span>
                        {hasFlowLicence && (
                          <Group spacing={2} noWrap>
                            {!isPrimarySubscription(index) && (
                              <ActionIcon
                                aria-label="Kalender-Abo umbenennen"
                                className="calendar-subscription-action"
                                color="gray"
                                size="xs"
                                variant="subtle"
                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                  event.stopPropagation();
                                  openEditCalendarSubscriptionModal(calendarSubscription);
                                }}
                              >
                                <IconEdit size={12} />
                              </ActionIcon>
                            )}
                            {!isPrimarySubscription(index) && (
                              <ActionIcon
                                aria-label="Kalender-Abo löschen"
                                className="calendar-subscription-action"
                                color="gray"
                                size="xs"
                                variant="subtle"
                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                  event.stopPropagation();
                                  openDeleteCalendarSubscriptionModal(calendarSubscription);
                                }}
                              >
                                <IconX size={12} />
                              </ActionIcon>
                            )}
                          </Group>
                        )}
                      </Group>
                    </Tabs.Tab>
                  ))}
                  <Tabs.Tab value={RECOMMENDATIONS_TAB}>Vermittlungen</Tabs.Tab>
                </Tabs.List>
                {hasFlowLicence && (
                  <Box mt={5}>
                    <Tooltip label="Kalender-Abo hinzufügen" withArrow>
                      <Box>
                        <BookitupAddIcon disabled={loading} onClick={openAddCalendarSubscriptionModal} />
                      </Box>
                    </Tooltip>
                  </Box>
                )}
              </Group>
            </Tabs>

            <Stack mt="sm" spacing="md">
              {subscription && (
                <>
                  <Subscription
                    calendarSubscriptionId={subscription.id}
                    entityType="events"
                    label="Kalender-URL für Buchungen"
                  />

                  <ConfigOption
                    title="Eigenen Buchungsstatus verwenden"
                    description="Kalendereinträge zeigen Deinen individuell erstellten Buchungsstatus."
                    rightSection={
                      <Switch
                        checked={subscription.useCustomStateLabels}
                        disabled={savingField === 'useCustomStateLabels'}
                        onChange={() => editCalendar({ useCustomStateLabels: !subscription.useCustomStateLabels })}
                      />
                    }
                  />
                  <ConfigOption
                    title="Event-Tag-Beschreibung verwenden"
                    description="Kalendereinträge enthalten die Beschreibung des jeweiligen Event-Tags."
                    rightSection={
                      <Switch
                        checked={subscription.useEventDateDescription}
                        disabled={savingField === 'useEventDateDescription'}
                        onChange={() =>
                          editCalendar({ useEventDateDescription: !subscription.useEventDateDescription })
                        }
                      />
                    }
                  />

                  {canConfigureSubscription && (
                    <>
                      <Divider label={<ThemeText>Sichtbarkeit</ThemeText>} labelPosition="center" />

                      {visibilityScopeOptions.map((option) => (
                        <ConfigOption
                          key={option.value}
                          title={option.title}
                          description={option.description}
                          rightSection={
                            <Switch
                              checked={visibilityScopes.includes(option.value)}
                              disabled={savingVisibilityScope === option.value}
                              onChange={() => toggleVisibilityScope(option.value)}
                            />
                          }
                        />
                      ))}
                      {visibilityScopes.includes(VisibilityScope.LOCATION) && (
                        <ConfigOption
                          title={onlyLocationCityVisibilityScopeOption.title}
                          description={onlyLocationCityVisibilityScopeOption.description}
                          rightSection={
                            <Switch
                              checked={visibilityScopes.includes(onlyLocationCityVisibilityScopeOption.value)}
                              disabled={
                                savingVisibilityScope === onlyLocationCityVisibilityScopeOption.value ||
                                savingVisibilityScope === VisibilityScope.LOCATION
                              }
                              onChange={() => toggleVisibilityScope(onlyLocationCityVisibilityScopeOption.value)}
                            />
                          }
                        />
                      )}
                      <MultiSelect
                        label={<ThemeText size="sm">Eigene Felder</ThemeText>}
                        description="Ausgewählte eigene Felder werden in die Beschreibung der Kalendereinträge übernommen."
                        placeholder="Eigene Felder auswählen"
                        searchable
                        clearable
                        data={customAttributeOptions}
                        value={customFields}
                        disabled={customAttributesLoading || savingField === 'customFields'}
                        icon={customAttributesLoading && <Loader size="xs" />}
                        rightSection={<IconChevronDown size={14} />}
                        rightSectionWidth={25}
                        onChange={(selectedFields) =>
                          editCalendar({
                            customFields: selectedFields.filter((fieldName) =>
                              allowedCustomFieldNames.includes(fieldName),
                            ),
                          })
                        }
                        styles={{ rightSection: { pointerEvents: 'none' } }}
                      />

                      <Divider label={<ThemeText>Filter-Einstellungen</ThemeText>} labelPosition="center" />

                      <MultiSelect
                        label={<ThemeText size="sm">Buchungsstatus</ThemeText>}
                        description="Nur Buchungen mit diesen Basis-Status werden in den Kalender eingetragen."
                        placeholder="Status auswählen"
                        searchable
                        clearable
                        data={bookingStateOptions}
                        value={statesOfInterest}
                        disabled={savingField === 'statesOfInterest'}
                        rightSection={<IconChevronDown size={14} />}
                        rightSectionWidth={25}
                        onChange={(selectedStates) =>
                          editCalendar({
                            statesOfInterest: filterCalendarBookingStates(selectedStates as BookingState[]),
                          })
                        }
                        styles={{ rightSection: { pointerEvents: 'none' } }}
                      />
                      <MultiSelect
                        label={<ThemeText size="sm">Eigene Buchungsstatus</ThemeText>}
                        description="Optional kannst Du zusätzlich einzelne eigene Status auswählen."
                        placeholder="Eigene Status auswählen"
                        searchable
                        clearable
                        data={customStateOptions}
                        value={customStatesOfInterest}
                        disabled={eventStatesLoading || savingField === 'customStatesOfInterest'}
                        icon={eventStatesLoading && <Loader size="xs" />}
                        rightSection={<IconChevronDown size={14} />}
                        rightSectionWidth={25}
                        onChange={(selectedStates) =>
                          editCalendar({
                            customStatesOfInterest: selectedStates.filter((stateId) =>
                              allowedCustomStateIds.includes(stateId),
                            ),
                          })
                        }
                        styles={{ rightSection: { pointerEvents: 'none' } }}
                      />
                      <MultiSelect
                        label={<ThemeText size="sm">Auftragsarten</ThemeText>}
                        description="Leer lassen, wenn alle Auftragsarten im Kalender erscheinen sollen."
                        placeholder="Auftragsarten auswählen"
                        searchable
                        clearable
                        data={DataS.getEventKindsOptions(kinds)}
                        value={subscription.kindsOfInterest ?? []}
                        disabled={eventKindsLoading || savingField === 'kindsOfInterest'}
                        icon={eventKindsLoading && <Loader size="xs" />}
                        rightSection={<IconChevronDown size={14} />}
                        rightSectionWidth={25}
                        onChange={(selectedKinds) => editCalendar({ kindsOfInterest: selectedKinds })}
                        styles={{ rightSection: { pointerEvents: 'none' } }}
                      />
                    </>
                  )}
                </>
              )}

              {activeSubscriptionId === RECOMMENDATIONS_TAB && recommendationsSubscription && (
                <>
                  <Subscription
                    calendarSubscriptionId={recommendationsSubscription.id}
                    entityType="recommendations"
                    label="Kalender-URL für Vermittlungen"
                  />
                  <ConfigOption
                    title="Nur geteilte Vermittlungen eintragen"
                    description="Es werden nur Vermittlungen übernommen, deren Auftragsdaten mit Dir geteilt wurden."
                    rightSection={
                      <Switch
                        checked={recommendationsSubscription.showOnlySharedRecommendations}
                        disabled={savingField === 'showOnlySharedRecommendations'}
                        onChange={() =>
                          editCalendar(
                            {
                              showOnlySharedRecommendations: !recommendationsSubscription.showOnlySharedRecommendations,
                            },
                            recommendationsSubscription,
                          )
                        }
                      />
                    }
                  />
                </>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

interface CreateCalendarSubscriptionModalProps {
  defaultName: string;
  onCreate: (calendarSubscription: CalendarSubscription) => void;
}

const openCreateCalendarSubscriptionModal = (props: CreateCalendarSubscriptionModalProps) =>
  openModal({
    title: (
      <ThemeText size="xl" weight={600}>
        Kalender-Abo hinzufügen
      </ThemeText>
    ),
    children: <CreateCalendarSubscriptionModal defaultName={props.defaultName} onCreate={props.onCreate} />,
  });

const CreateCalendarSubscriptionModal: FC<CreateCalendarSubscriptionModalProps> = ({ defaultName, onCreate }) => {
  const [name, setName] = useState(defaultName);
  const [loading, setLoading] = useState(false);

  const create = async () => {
    const trimmedName = name.trim();
    setLoading(true);
    try {
      const calendarSubscription = await CalDavS.createCalendarSubscription({
        createNew: true,
        name: trimmedName,
      });
      if (calendarSubscription) {
        onCreate(calendarSubscription);
        closeAllModals();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TextInput
        autoFocus
        label="Name"
        value={name}
        onChange={(event) => setName(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && name.trim() && !loading) {
            create();
          }
        }}
      />
      <Center mt="xl">
        <Button disabled={!name.trim() || loading} rightIcon={loading && <Loader size="xs" />} onClick={create}>
          Hinzufügen
        </Button>
      </Center>
    </>
  );
};

interface CalendarSubscriptionNameModalProps {
  calendarSubscription: CalendarSubscription;
  onChange: (calendarSubscription: CalendarSubscription) => void;
}

const openCalendarSubscriptionNameModal = (props: CalendarSubscriptionNameModalProps) =>
  openModal({
    title: (
      <ThemeText size="xl" weight={600}>
        Kalender-Abo umbenennen
      </ThemeText>
    ),
    children: (
      <CalendarSubscriptionNameModal calendarSubscription={props.calendarSubscription} onChange={props.onChange} />
    ),
  });

const CalendarSubscriptionNameModal: FC<CalendarSubscriptionNameModalProps> = ({ calendarSubscription, onChange }) => {
  const [name, setName] = useState(calendarSubscription.name);
  const [loading, setLoading] = useState(false);

  const save = async () => {
    const trimmedName = name.trim();
    setLoading(true);
    try {
      const nextSubscription = await CalDavS.editCalendarSubscription(calendarSubscription.id, {
        name: trimmedName,
      });
      if (nextSubscription) {
        onChange(nextSubscription);
        closeAllModals();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TextInput
        autoFocus
        label="Name"
        value={name}
        onChange={(event) => setName(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && name.trim() && !loading) {
            save();
          }
        }}
      />
      <Center mt="xl">
        <Button
          disabled={!name.trim() || name.trim() === calendarSubscription.name || loading}
          rightIcon={loading && <Loader size="xs" />}
          onClick={save}
        >
          Speichern
        </Button>
      </Center>
    </>
  );
};

export { SubscriptionCalendar };
export default SubscriptionCalendar;
